/** Process Design — shape constants and export helpers (mockup v0.37). */

export const LANE_HEIGHT = 130;
export const CANVAS_W = 1500;
export const DEFAULT_LANES = ['Process owner', 'System'];

export const PRESETS = [
  { key: 'neutral', bg: '#ffffff', border: '#d3d1c7', text: '#2c2c2a' },
  { key: 'purple', bg: '#EEEDFE', border: '#AFA9EC', text: '#3C3489' },
  { key: 'green', bg: '#e1f5ee', border: '#5dcaa5', text: '#085041' },
  { key: 'amber', bg: '#faeeda', border: '#fac775', text: '#854F0B' },
  { key: 'red', bg: '#FCEBEB', border: '#F5ACAB', text: '#A32D2D' },
  { key: 'blue', bg: '#E3EFFA', border: '#8fb9df', text: '#185FA5' },
  { key: 'yellow', bg: '#FFF8DE', border: '#E8C84A', text: '#6b5a10' },
];

export const BUILTIN_SHAPE_DEFS = {
  start: { label: 'Start', base: 'pill', color: PRESETS[2] },
  step: { label: 'Process step', base: 'rect', color: PRESETS[0] },
  decision: { label: 'Decision', base: 'diamond', color: PRESETS[0] },
  output: { label: 'Output', base: 'document', color: PRESETS[5] },
  subprocess: { label: 'Predefined process', base: 'predefined', color: PRESETS[1] },
  end: { label: 'End', base: 'pill', color: PRESETS[4] },
  remark: { label: 'Remark', base: 'note', color: PRESETS[6] },
};

export const BASE_GEOMETRIES = [
  { key: 'rect', label: 'Rectangle — process' },
  { key: 'diamond', label: 'Diamond — decision' },
  { key: 'parallelogram', label: 'Parallelogram — data / input-output' },
  { key: 'document', label: 'Document — output / report' },
  { key: 'predefined', label: 'Predefined process — sub-process reference' },
  { key: 'pill', label: 'Rounded pill — terminator (start/end)' },
  { key: 'hexagon', label: 'Hexagon — preparation' },
  { key: 'trapezoid', label: 'Trapezoid — manual input' },
  { key: 'card', label: 'Card — punched card / exception' },
  { key: 'pentagon', label: 'Pentagon — off-page connector' },
  { key: 'circle', label: 'Circle — connector / junction' },
  { key: 'cylinder', label: 'Cylinder — database / stored data' },
  { key: 'triangle-up', label: 'Triangle (up) — extract' },
  { key: 'triangle-down', label: 'Triangle (down) — merge' },
  { key: 'display', label: 'Display — screen output' },
  { key: 'note', label: 'Sticky note — remark' },
];

export const FLOW_STATUS_LABEL = {
  not_drafted: 'Not drafted',
  draft: 'Draft',
  review: 'In review',
  published: 'Published',
};

export function presetByKey(k) {
  return PRESETS.find((p) => p.key === k) || PRESETS[0];
}

export function luminanceText(hex) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substr(0, 2), 16);
  const g = parseInt(c.substr(2, 2), 16);
  const b = parseInt(c.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150 ? '#2c2c2a' : '#ffffff';
}

export function shapeDef(type, customShapeTypes = []) {
  if (BUILTIN_SHAPE_DEFS[type]) return BUILTIN_SHAPE_DEFS[type];
  const custom = customShapeTypes.find((t) => t.id === type);
  if (custom) {
    return {
      label: custom.label,
      base: custom.base_geometry || custom.base,
      color: custom.color,
    };
  }
  return null;
}

export function shapeSize(type, customShapeTypes = []) {
  const def = shapeDef(type, customShapeTypes) || { base: 'rect' };
  const compact = { w: 76, h: 76 };
  const sizes = {
    diamond: { w: 110, h: 90 },
    pill: { w: 100, h: 44 },
    note: { w: 150, h: 78 },
    parallelogram: { w: 150, h: 56 },
    document: { w: 150, h: 62 },
    predefined: { w: 160, h: 56 },
    hexagon: { w: 150, h: 60 },
    trapezoid: { w: 150, h: 56 },
    card: { w: 150, h: 56 },
    pentagon: { w: 150, h: 70 },
    circle: compact,
    cylinder: { w: 110, h: 80 },
    'triangle-up': compact,
    'triangle-down': compact,
    display: { w: 150, h: 66 },
  };
  return sizes[def.base] || { w: 150, h: 56 };
}

export function shapeCenter(shape, customShapeTypes = []) {
  const { w, h } = shapeSize(shape.type, customShapeTypes);
  return { x: Number(shape.x) + w / 2, y: Number(shape.y) + h / 2, w, h };
}

export function deltaClass(activeView, shape) {
  if (activeView !== 'delta') return '';
  if (shape.delta_status === 'added') return 'delta-added';
  if (shape.delta_status === 'changed') return 'delta-changed';
  if (shape.delta_status === 'removed') return 'delta-removed';
  return '';
}

export function flowStatusBadgeClass(status) {
  if (status === 'published') return 'published';
  if (status === 'review') return 'review';
  if (status === 'draft') return 'draft';
  return 'notdrafted';
}

/** Map DB row → canvas shape object. */
export function dbShapeToCanvas(row, buildItemsById = new Map()) {
  const bi = row.build_item_id ? buildItemsById.get(row.build_item_id) : null;
  return {
    id: row.id,
    type: row.custom_shape_type_id || row.type,
    label: row.label || '',
    lane: row.lane,
    x: Number(row.x),
    y: Number(row.y),
    color: row.color,
    build_item_id: row.build_item_id,
    ref_text: row.ref_text,
    delta_status: row.delta_status,
    ref: bi?.ref || row.ref_text || null,
  };
}

/** Map canvas shape → DB insert/update payload. */
export function canvasShapeToDb(shape, processFlowId) {
  const isBuiltin = Boolean(BUILTIN_SHAPE_DEFS[shape.type]);
  return {
    process_flow_id: processFlowId,
    type: isBuiltin ? shape.type : 'custom',
    custom_shape_type_id: isBuiltin ? null : shape.type,
    lane: shape.lane,
    x: shape.x,
    y: shape.y,
    label: shape.label,
    color: shape.color || null,
    build_item_id: shape.build_item_id || null,
    ref_text: shape.ref_text || null,
    delta_status: shape.delta_status || null,
  };
}

export function downloadFile(content, filename, mime) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function xmlEscape(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function bpmnElementTag(type, customShapeTypes) {
  const def = shapeDef(type, customShapeTypes);
  if (type === 'start') return 'startEvent';
  if (type === 'end') return 'endEvent';
  if (type === 'subprocess' || (def && def.base === 'predefined')) return 'subProcess';
  if (type === 'decision' || (def && def.base === 'diamond')) return 'exclusiveGateway';
  if (type === 'remark' || (def && def.base === 'note')) return 'textAnnotation';
  return 'task';
}

export function exportTransformaJson({ nodeId, nodeName, lanes, diagrams }) {
  return JSON.stringify(
    { version: 1, nodeId, nodeName, lanes, diagrams },
    null,
    2
  );
}

export function exportBpmn({
  nodeId,
  nodeName,
  lanes,
  diagram,
  activeView,
  customShapeTypes,
}) {
  const laneShapeIds = {};
  lanes.forEach((l) => { laneShapeIds[l] = []; });
  let shapeXml = '';
  let diShapeXml = '';
  let diEdgeXml = '';
  let flowXml = '';
  let annotationXml = '';
  let associationXml = '';

  diagram.shapes.forEach((s) => {
    const { w, h } = shapeSize(s.type, customShapeTypes);
    if (s.lane && laneShapeIds[s.lane]) laneShapeIds[s.lane].push(s.id);
    if (bpmnElementTag(s.type, customShapeTypes) === 'textAnnotation') {
      annotationXml += `<textAnnotation id="${s.id}"><text>${xmlEscape(s.label)}</text></textAnnotation>`;
    } else {
      const tag = bpmnElementTag(s.type, customShapeTypes);
      shapeXml += `<${tag} id="${s.id}" name="${xmlEscape(s.label)}"/>`;
    }
    const laneIdx = Math.max(0, lanes.indexOf(s.lane || lanes[0]));
    diShapeXml += `<bpmndi:BPMNShape id="${s.id}_di" bpmnElement="${s.id}"><dc:Bounds x="${s.x}" y="${s.y + laneIdx * LANE_HEIGHT}" width="${w}" height="${h}"/></bpmndi:BPMNShape>`;
  });

  diagram.connections.forEach((c) => {
    const fromShape = diagram.shapes.find((x) => x.id === c.from);
    const isAnnotationLink = fromShape && bpmnElementTag(fromShape.type, customShapeTypes) === 'textAnnotation';
    if (isAnnotationLink) {
      associationXml += `<association id="assoc_${c.id}" sourceRef="${c.from}" targetRef="${c.to}"/>`;
    } else {
      flowXml += `<sequenceFlow id="${c.id}" sourceRef="${c.from}" targetRef="${c.to}"${c.label ? ` name="${xmlEscape(c.label)}"` : ''}/>`;
      diEdgeXml += `<bpmndi:BPMNEdge id="${c.id}_di" bpmnElement="${c.id}"/>`;
    }
  });

  const laneSetXml = `<laneSet id="laneSet_1">${lanes.map((l, i) =>
    `<lane id="lane_${i}" name="${xmlEscape(l)}"><flowNodeRef>${(laneShapeIds[l] || []).join('</flowNodeRef><flowNodeRef>')}</flowNodeRef></lane>`
  ).join('')}</laneSet>`;

  return `<?xml version="1.0" encoding="UTF-8"?>
<definitions xmlns="http://www.omg.org/spec/BPMN/20100524/MODEL" xmlns:bpmndi="http://www.omg.org/spec/BPMN/20100524/DI" xmlns:dc="http://www.omg.org/spec/DD/20100524/DC" id="Definitions_${nodeId}" targetNamespace="https://transforma.app/bpmn">
  <process id="Process_${nodeId}_${activeView}" name="${xmlEscape(nodeName)} (${activeView})" isExecutable="false">
    ${laneSetXml}
    ${shapeXml}
    ${annotationXml}
    ${flowXml}
    ${associationXml}
  </process>
  <bpmndi:BPMNDiagram id="Diagram_1"><bpmndi:BPMNPlane id="Plane_1" bpmnElement="Process_${nodeId}_${activeView}">${diShapeXml}${diEdgeXml}</bpmndi:BPMNPlane></bpmndi:BPMNDiagram>
</definitions>`;
}

function mermaidShapeWrap(s, customShapeTypes) {
  const label = s.label.replace(/"/g, "'");
  const base = (shapeDef(s.type, customShapeTypes) || {}).base;
  if (base === 'pill') return `(["${label}"])`;
  if (base === 'diamond') return `{"${label}"}`;
  if (base === 'predefined') return `[["${label}"]]`;
  if (base === 'cylinder') return `[("${label}")]`;
  if (base === 'circle') return `(("${label}"))`;
  if (base === 'parallelogram') return `[/"${label}"/]`;
  if (base === 'hexagon') return `{{"${label}"}}`;
  if (base === 'note') return `>"${label}"]`;
  return `["${label}"]`;
}

export function exportMermaid({ lanes, diagram, customShapeTypes }) {
  let out = 'flowchart LR\n';
  lanes.forEach((laneName, i) => {
    out += `  subgraph lane${i} ["${laneName}"]\n`;
    diagram.shapes.filter((s) => s.lane === laneName).forEach((s) => {
      out += `    ${s.id}${mermaidShapeWrap(s, customShapeTypes)}\n`;
    });
    out += '  end\n';
  });
  const noLane = diagram.shapes.filter((s) => !s.lane);
  if (noLane.length) {
    out += '  subgraph notes ["Remarks"]\n';
    noLane.forEach((s) => { out += `    ${s.id}${mermaidShapeWrap(s, customShapeTypes)}\n`; });
    out += '  end\n';
  }
  diagram.connections.forEach((c) => {
    out += c.label ? `  ${c.from} -- ${c.label} --> ${c.to}\n` : `  ${c.from} --> ${c.to}\n`;
  });
  return out;
}

/** Parse imported Transforma JSON into lane + diagram updates. */
export function parseTransformaImport(json) {
  const data = typeof json === 'string' ? JSON.parse(json) : json;
  if (!data.diagrams) throw new Error('missing diagrams');
  return {
    lanes: data.lanes,
    diagrams: {
      current: {
        shapes: (data.diagrams.current?.shapes || []).map((s) => ({ ...s })),
        connections: (data.diagrams.current?.connections || []).map((c) => ({ ...c, from: c.from, to: c.to })),
      },
      future: {
        shapes: (data.diagrams.future?.shapes || []).map((s) => ({ ...s })),
        connections: (data.diagrams.future?.connections || []).map((c) => ({ ...c, from: c.from, to: c.to })),
      },
    },
  };
}

/**
 * Convert portable canvas JSON to the database-shaped payload consumed by the
 * atomic import RPC. Imported build-item UUIDs are never trusted across
 * projects; references are resolved against the current project's rows.
 */
export function prepareProcessDesignImport(parsed, buildItems = [], fallbackLanes = DEFAULT_LANES) {
  const importedLanes = parsed?.lanes ?? fallbackLanes;
  if (
    !Array.isArray(importedLanes)
    || importedLanes.length === 0
    || importedLanes.some((lane) => typeof lane !== 'string' || !lane.trim())
  ) {
    throw new Error('invalid lanes');
  }

  const buildItemsById = new Map(buildItems.map((item) => [item.id, item]));
  const buildItemsByRef = new Map(
    buildItems.filter((item) => item.ref).map((item) => [item.ref, item])
  );
  const diagrams = {};

  for (const state of ['current', 'future']) {
    const diagram = parsed?.diagrams?.[state];
    if (!diagram || !Array.isArray(diagram.shapes) || !Array.isArray(diagram.connections)) {
      throw new Error(`invalid ${state} diagram`);
    }

    const importIds = new Set();
    const shapes = diagram.shapes.map((shape) => {
      const importId = shape?.id == null ? '' : String(shape.id);
      if (!importId || importIds.has(importId)) {
        throw new Error(`invalid ${state} shape id`);
      }
      importIds.add(importId);

      const reference = shape.ref || shape.ref_text || null;
      const linkedBuildItem = (reference && buildItemsByRef.get(reference))
        || (shape.build_item_id && buildItemsById.get(shape.build_item_id))
        || null;
      const dbShape = canvasShapeToDb({
        ...shape,
        build_item_id: linkedBuildItem?.id || null,
        ref_text: linkedBuildItem ? null : reference,
        delta_status: shape.delta || shape.delta_status || null,
      }, null);
      delete dbShape.process_flow_id;
      return { import_id: importId, ...dbShape };
    });

    const connections = diagram.connections.map((connection) => {
      const from = connection?.from == null ? '' : String(connection.from);
      const to = connection?.to == null ? '' : String(connection.to);
      if (!importIds.has(from) || !importIds.has(to)) {
        throw new Error(`invalid ${state} connection`);
      }
      return { from, to, label: connection.label || null };
    });

    diagrams[state] = { shapes, connections };
  }

  return {
    lanes: importedLanes.map((lane) => lane.trim()),
    diagrams,
  };
}
